import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Bolt as BoltIcon,
  Whatshot as HotSeatIcon,
  EmojiEvents as TrophyIcon,
  CallSplit as SplitIcon,
  Groups as GroupsIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { BEER, LobbyStage, BrandLockup, Dots } from "./BeerillionerLobbyStage";

const GLASS_BUBBLES = Array.from({ length: 9 }, (_, i) => {
  const r = ((i * 7717 + 3319) % 9973) / 9973;
  return {
    left: `${12 + r * 68}%`,
    size: 4 + Math.round(r * 5),
    duration: `${1.6 + r * 1.8}s`,
    delay: `${(r * 2.4).toFixed(2)}s`,
  };
});

const ROUNDS = [
  { icon: BoltIcon, color: BEER.blue, label: "Fastest Finger", sub: "Round 1" },
  { icon: HotSeatIcon, color: BEER.amber, label: "Hot Seat", sub: "Round 2" },
  { icon: TrophyIcon, color: BEER.gold, label: "The Ladder", sub: "Round 3" },
];

const lifelineIcon = (type) =>
  type === "50_50" ? SplitIcon : type === "ask_audience" ? GroupsIcon : PhoneIcon;

/** Pint filling up — the visual metaphor for "we're about to start". */
const PouringGlass = () => (
  <Box
    sx={{
      position: "relative",
      width: { xs: 96, sm: 112 },
      height: { xs: 128, sm: 150 },
      animation: "beerFadeUp 0.9s ease 0.1s both",
      "@keyframes beerGlassBubble": {
        "0%": { transform: "translateY(0) scale(0.6)", opacity: 0 },
        "20%": { opacity: 0.9 },
        "100%": { transform: "translateY(-88px) scale(1)", opacity: 0 },
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: "-22%",
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(255,215,0,0.24) 0%, rgba(255,152,0,0.1) 48%, transparent 72%)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        right: { xs: -22, sm: -26 },
        top: "26%",
        width: { xs: 24, sm: 28 },
        height: { xs: 46, sm: 54 },
        border: "3px solid rgba(255,255,255,0.32)",
        borderLeft: "none",
        borderRadius: "0 16px 16px 0",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "12px 12px 26px 26px",
        border: "3px solid rgba(255,255,255,0.34)",
        bgcolor: "rgba(255,255,255,0.04)",
        overflow: "hidden",
        boxShadow: "inset 0 0 24px rgba(255,215,0,0.12)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "88%",
          background: "linear-gradient(to top, #f57c00 0%, #ff9800 40%, #ffc107 75%, #ffd700 100%)",
          animation: "beerFill 2.6s cubic-bezier(0.33, 1, 0.68, 1) 0.3s both",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -9,
            left: -4,
            right: -4,
            height: 20,
            borderRadius: "50%",
            background: "linear-gradient(to bottom, #fffdf5, #ffe9b8)",
            boxShadow: "0 0 16px rgba(255,255,255,0.35)",
            animation: "beerFoamBob 2.2s ease-in-out infinite",
          }}
        />
        {GLASS_BUBBLES.map((b, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              bottom: 4,
              left: b.left,
              width: b.size,
              height: b.size,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.6)",
              animation: `beerGlassBubble ${b.duration} linear ${b.delay} infinite`,
            }}
          />
        ))}
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: "8%",
          left: "16%",
          width: 8,
          borderRadius: 4,
          height: "70%",
          bgcolor: "rgba(255,255,255,0.22)",
        }}
      />
    </Box>
  </Box>
);

/** Compact prize ladder for the lobby — highest rung first. */
const LobbyPrizeLadder = ({ prizes = [], apiBase = "" }) => {
  if (!prizes.length) return null;
  const ordered = [...prizes].sort((a, b) => (b.level || 0) - (a.level || 0));

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 420,
        mt: { xs: 2.6, sm: 3.2 },
        animation: "beerFadeUp 0.8s ease 1.25s both",
      }}
    >
      <Typography
        sx={{
          color: BEER.gold,
          fontWeight: 800,
          letterSpacing: 2,
          fontSize: { xs: "0.72rem", sm: "0.8rem" },
          mb: 1,
          textAlign: "center",
        }}
      >
        PRIZE LADDER
      </Typography>
      <Box
        sx={{
          bgcolor: "rgba(13,33,55,0.85)",
          borderRadius: "18px",
          border: `1px solid ${BEER.border}`,
          overflow: "hidden",
          maxHeight: { xs: 220, sm: 280 },
          overflowY: "auto",
          boxShadow: "0 0 24px rgba(255,215,0,0.08)",
        }}
      >
        {ordered.map((p, i) => {
          const img = p.image_url
            ? p.image_url.startsWith("http")
              ? p.image_url
              : `${apiBase}/${p.image_url}`
            : null;
          const isTop = i === 0;
          return (
            <Box
              key={p.id || p.level}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.75,
                py: { xs: 0.7, sm: 0.85 },
                bgcolor: isTop
                  ? "rgba(255,215,0,0.14)"
                  : p.is_checkpoint
                    ? "rgba(255,215,0,0.06)"
                    : "transparent",
                borderLeft: isTop
                  ? `3px solid ${BEER.gold}`
                  : p.is_checkpoint
                    ? `3px solid ${BEER.amber}`
                    : "3px solid transparent",
                borderBottom: i < ordered.length - 1 ? `1px solid ${BEER.border}` : "none",
              }}
            >
              {p.is_checkpoint ? (
                <Typography sx={{ color: BEER.gold, fontSize: "0.7rem", minWidth: 12 }}>◆</Typography>
              ) : (
                <Box sx={{ minWidth: 12 }} />
              )}
              <Typography
                sx={{
                  color: isTop ? BEER.gold : "#fff",
                  fontSize: { xs: "0.78rem", sm: "0.85rem" },
                  fontWeight: isTop ? 800 : 600,
                  minWidth: 22,
                }}
              >
                {p.level}
              </Typography>
              {img && (
                <Box
                  component="img"
                  src={img}
                  alt=""
                  sx={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }}
                />
              )}
              <Typography
                sx={{
                  color: isTop ? BEER.gold : "#fff",
                  fontSize: { xs: "0.78rem", sm: "0.85rem" },
                  fontWeight: isTop ? 800 : 500,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "left",
                }}
              >
                {p.label || p.value_text || `Level ${p.level}`}
              </Typography>
              {isTop && (
                <Typography
                  sx={{
                    color: BEER.gold,
                    fontSize: "0.58rem",
                    fontWeight: 800,
                    letterSpacing: 0.6,
                    flexShrink: 0,
                  }}
                >
                  TOP
                </Typography>
              )}
              {!isTop && p.is_checkpoint && (
                <Typography
                  sx={{
                    color: BEER.amber,
                    fontSize: "0.58rem",
                    fontWeight: 800,
                    letterSpacing: 0.6,
                    flexShrink: 0,
                  }}
                >
                  SAFE
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const BeerillionerGetReadyView = ({
  config,
  spectatorMode = false,
  apiBase = "",
}) => {
  const prizes = config?.prizes || [];
  const lifelines = (config?.lifelines || []).filter((l) => l.is_enabled);

  return (
    <LobbyStage>
      <BrandLockup kicker="TABLE'S SET FOR" size="sm" delay={0} />

      <Box sx={{ mt: { xs: 2, sm: 2.6 } }}>
        <PouringGlass />
      </Box>

      <Typography
        component="h2"
        sx={{
          mt: { xs: 2.4, sm: 3 },
          color: BEER.gold,
          fontWeight: 900,
          letterSpacing: { xs: 4, sm: 10 },
          fontSize: { xs: "2.1rem", sm: "3.1rem", md: "3.6rem" },
          lineHeight: 1,
          textShadow: "0 0 34px rgba(255,215,0,0.45)",
          animation: "beerFadeUp 0.8s ease 0.5s both, beerReadyPulse 2.4s ease-in-out 1.3s infinite",
          "@keyframes beerReadyPulse": {
            "0%, 100%": { textShadow: "0 0 30px rgba(255,215,0,0.4)", transform: "scale(1)" },
            "50%": { textShadow: "0 0 52px rgba(255,215,0,0.85)", transform: "scale(1.025)" },
          },
        }}
      >
        GET READY
      </Typography>

      <Typography
        sx={{
          color: "#fff",
          fontWeight: 600,
          fontSize: { xs: "0.98rem", sm: "1.2rem" },
          mt: { xs: 1, sm: 1.3 },
          animation: "beerFadeUp 0.8s ease 0.65s both",
        }}
      >
        {spectatorMode ? "The room is warming up." : "Thumbs on the glass."}
      </Typography>
      <Typography
        sx={{
          color: BEER.glow,
          fontSize: { xs: "0.85rem", sm: "0.98rem" },
          mt: 0.4,
          animation: "beerFadeUp 0.8s ease 0.75s both",
        }}
      >
        Your host is pouring the first round
        <Dots />
      </Typography>

      {/* Round roadmap — first stop is live next */}
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          gap: { xs: 0.6, sm: 1.2 },
          width: "100%",
          maxWidth: 560,
          mt: { xs: 2.8, sm: 3.4 },
        }}
      >
        {ROUNDS.map((r, i) => {
          const Icon = r.icon;
          const isNext = i === 0;
          return (
            <React.Fragment key={r.label}>
              {i > 0 && (
                <Box
                  sx={{
                    alignSelf: "center",
                    width: { xs: 10, sm: 20 },
                    height: 1,
                    bgcolor: BEER.border,
                    flexShrink: 0,
                  }}
                />
              )}
              <Box
                sx={{
                  flex: 1,
                  px: { xs: 0.8, sm: 1.4 },
                  py: { xs: 1.2, sm: 1.5 },
                  borderRadius: "18px",
                  bgcolor: isNext ? "rgba(41,182,246,0.12)" : "rgba(13,33,55,0.5)",
                  border: `1px solid ${isNext ? `${BEER.blue}88` : BEER.border}`,
                  boxShadow: isNext ? `0 0 22px ${BEER.blue}26` : "none",
                  opacity: isNext ? 1 : 0.62,
                  animation: `beerFadeUp 0.7s ease ${0.9 + i * 0.12}s both`,
                }}
              >
                <Icon sx={{ color: r.color, fontSize: { xs: 20, sm: 24 } }} />
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: { xs: "0.74rem", sm: "0.88rem" },
                    mt: 0.4,
                    lineHeight: 1.2,
                  }}
                >
                  {r.label}
                </Typography>
                <Typography
                  sx={{
                    color: isNext ? BEER.blue : "#6d8ba3",
                    fontSize: { xs: "0.6rem", sm: "0.68rem" },
                    fontWeight: 700,
                    letterSpacing: 1,
                    mt: 0.2,
                  }}
                >
                  {isNext ? "UP NEXT" : r.sub.toUpperCase()}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>

      <LobbyPrizeLadder prizes={prizes} apiBase={apiBase} />

      {lifelines.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 0.8,
            mt: { xs: 2.2, sm: 2.6 },
            animation: "beerFadeUp 0.7s ease 1.55s both",
          }}
        >
          <Typography
            sx={{ color: BEER.blue, fontWeight: 800, fontSize: "0.68rem", letterSpacing: 2, mr: 0.5 }}
          >
            LIFELINES READY
          </Typography>
          {lifelines.map((ll) => {
            const Icon = lifelineIcon(ll.type);
            return (
              <Box
                key={ll.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1.4,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: "rgba(255,215,0,0.08)",
                  border: "1px solid rgba(255,215,0,0.3)",
                }}
              >
                <Icon sx={{ color: BEER.gold, fontSize: 15 }} />
                <Typography sx={{ color: BEER.gold, fontSize: "0.72rem", fontWeight: 700 }}>
                  {ll.label || ll.type}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </LobbyStage>
  );
};

export default BeerillionerGetReadyView;
